import './utils/buttons.css';
import { isContractDisabled, getStatusColor } from './utils/contractStatus';
import { isValidDate, formatDateWithoutTimezone, loadGuarantorsFromParticipants, loadFees, sanitizeFileName } from './utils/operationHelpers';
import { mapReceivableFromApi, mapReceivableToPayload, mapReceivablesFromSchema, buildInstallmentPayload, mapDisbursementToPayload } from './utils/dataMappers';
import { mapAcknowledgementDebtApiResponseToForm } from './utils/responseMappers';
import { buildAcknowledgementDebtPayload } from './utils/acknowledgementDebtPayloadBuilder';
import { useOperationData } from './hooks/useOperationData';
import { useProductManagement } from './hooks/useProductManagement';
import { useReceivablesDownloader } from './hooks/useReceivablesDownloader';
import { useContractSubmit } from './hooks/useContractSubmit';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { useCallback, useEffect, useState, Fragment } from "react";
import ConfirmationModal from "../../templates/ConfirmationModal";
import PageForm from "../../templates/PageForm";
import TextEdit from "../../molecules/TextEdit";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import { applyDateMask } from '../../../utils/dateMask';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { FaTimes, FaSearch } from "react-icons/fa";
import BadgeStatus from "../../atoms/BadgeStatus";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";
import ReceivableModalView from "./modals/ReceivableModalView";

import ArtifactFilesUpload from './components/ArtifactFilesUpload';
import PersonSearchModal from "./modals/PersonSearchModal";
import { AcknowledgementDebtForm, ReceivableItem, BaseCalculation } from './types/operations';
import { PersonSearchResult, ContractApiResponse, ApiListResponse, PersonListItem, ProductResponse, ProvinceResponse, CityResponse, JsonUploadData } from './types/api-responses';
import DisbursementManager from './components/DisbursementManager';
import FeesManager from './components/FeesManager';
import InstallmentManager from './components/InstallmentManager';
import GuarantorsManager from './components/GuarantorsManager';
import ReceivablesManager from './components/ReceivablesManager';
import ConvertReceivablesToInstallment from './components/ConvertReceivablesToInstallment';
import { useFileDownload } from './components/useFileDownload';
import appConfig from '../../../config/app-config';
import { CREDIT_OPERATION_TYPE_OPTIONS } from './constants/creditOperationTypes';
import { RECEIVING_ACCOUNT_OPTIONS } from './constants/receivingAccountOptions';
import { generateParticipants } from './utils/participantUtils';
import { useContractActions } from './hooks/useContractActions';
import BaseCalculationSection from './components/BaseCalculationSection';
import BillingConfigSection, { EMPTY_BILLING_CONFIG } from './components/BillingConfigSection';
import AssignorBillingAddress, { EMPTY_ASSIGNOR_BILLING_ADDRESS } from './components/AssignorBillingAddress';
import ContractActionDropdown from './components/ContractActionDropdown';


const { address: CONTRACTS_API_URL, key: CONTRACTS_API_KEY } = appConfig.apis.contractsManagementApiEnv;
const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;

const OPERATIONS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

const AcknowledgementDebtPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const operationType = searchParams.get('operation_type') || 'ACKNOWLEDGEMENT_OF_DEBT';
    const credit_operation_id = params.credit_operation_id || params.id;
    const { handleDownload } = useFileDownload();
    const artifactFiles = useSignal<File[]>([]);
    const contractFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    const [hasInvalidDueDates, setHasInvalidDueDates] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);
    const [lastInstallmentDueDate, setLastInstallmentDueDate] = useState<string | undefined>(undefined);
    const apiVersion = useSignal('');

    const form = useSignal<AcknowledgementDebtForm>({
        assignor: '',
        recipient: '',
        total_amount: 0,
        loan_amount: 0,
        expiration_date: '',
        emission_date: '',
        approved_on: '',
        operation_number: '',
        external_link: '',
        rebate_amount: 0,
        guarantee_value: null,
        status: '',
        sequencial_number: 0,
        originator: '',
        operation_uf: '',
        operation_city: '',
        original_contract_discharged: [],
        credit_product_type_name: '',
        receiving_account_name: '',
        crop_type: {
            name: '',
            id: ''
        },
        receivables: {
            quantity: 0,
            total: 0,
            items: [],
            overcollateral: null
        },
        disbursement: {
            totalAmount: 0,
            disbursements: []
        },
        installments: [],
        installment_format: 'VALOR',
        guarantors: {
            items: [],
            quantity: 0
        },
        main_risk: {
            uuid: '',
            name: ''
        },
        fees: {
            items: [],
            quantity: 0
        },
        artifacts: [],
        signed_contract_file: {
            file_id: '',
            file_name: ''
        },
        base_calculation: {
            tir_annual: null,
            tir_monthly: null,
            spread_annual: null,
            vpl: null,
            vpl_margin: null,
            duration_days: null
        } as BaseCalculation,
        billing_config: EMPTY_BILLING_CONFIG
    });

    const isLoading = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);

    const updateForm = (updater: Partial<AcknowledgementDebtForm>) => {
        form.value = { ...form.value, ...updater };
        setHasUnsavedChanges(true);
    };

    const [selectedDestinatario, setSelectedDestinatario] = useState('');

    const validateAllFields = useCallback((): boolean => {
        const f = form.value;
        const rules: Array<{ condition: boolean; label: string; fieldName: string }> = [
            { condition: !f.original_contract_discharged || f.original_contract_discharged.length === 0, label: 'Contratos Originais Quitados', fieldName: 'original_contract_discharged' },
            { condition: !f.external_link?.trim(), label: 'Card JIRA', fieldName: 'external_link' },
            { condition: !f.credit_product_type_name?.trim(), label: 'Tipo de Operação de Crédito', fieldName: 'credit_product_type_name' },
            { condition: !f.receiving_account_name?.trim(), label: 'Conta para Recebimento', fieldName: 'receiving_account_name' },
            { condition: !f.operation_uf?.trim(), label: 'UF da Operação', fieldName: 'operation_uf' },
            { condition: !f.operation_city?.trim(), label: 'Cidade da Operação', fieldName: 'operation_city' },
            { condition: !f.emission_date?.trim(), label: 'Data de Emissão', fieldName: 'emission_date' },
            { condition: !f.approved_on?.trim(), label: 'Data de Aprovação', fieldName: 'approved_on' },
            { condition: !selectedDestinatario || selectedDestinatario === '00000000-0000-0000-0000-000000000000', label: 'Beneficiário', fieldName: 'recipient' },
            { condition: !f.loan_amount, label: 'Valor Financiado', fieldName: 'loan_amount' },
            { condition: !f.total_amount, label: 'Valor Nominal', fieldName: 'nominal_value' },
            { condition: !f.disbursement.totalAmount || f.disbursement.totalAmount <= 0, label: 'Desembolsos', fieldName: 'disbursement' },
            { condition: f.billing_config?.generate_billets === null, label: 'Gerar boletos para cobrança', fieldName: 'generate_billets' },
            { condition: f.billing_config?.automatic_preventive_collection === null, label: 'Cobrança preventiva automática', fieldName: 'automatic_preventive_collection' },
            { condition: f.billing_config?.automatic_overdue_collection === null, label: 'Cobrança de vencidos automática', fieldName: 'automatic_overdue_collection' },
            { condition: f.receivables?.overcollateral === null || f.receivables?.overcollateral === undefined, label: 'Operação com overcollateral', fieldName: 'overcollateral' },
            { condition: !f.assignor_billing_address?.name?.trim(), label: 'Nome do Cedente (Faturamento)', fieldName: 'billing-name' },
            { condition: !f.assignor_billing_address?.document_number?.trim(), label: 'Documento do Cedente (Faturamento)', fieldName: 'billing-document' },
            { condition: !f.assignor_billing_address?.address_street?.trim(), label: 'Logradouro (Faturamento)', fieldName: 'billing-street' },
            { condition: !f.assignor_billing_address?.address_number?.trim(), label: 'Número (Faturamento)', fieldName: 'billing-number' },
            { condition: !f.assignor_billing_address?.address_neighborhood?.trim(), label: 'Bairro (Faturamento)', fieldName: 'billing-neighborhood' },
            { condition: !f.assignor_billing_address?.address_city?.trim(), label: 'Cidade (Faturamento)', fieldName: 'billing-city' },
            { condition: !f.assignor_billing_address?.address_state?.trim() || (f.assignor_billing_address?.address_state?.trim().length !== 2), label: 'UF (Faturamento)', fieldName: 'billing-state' },
            { condition: !f.assignor_billing_address?.address_zip_code?.trim(), label: 'CEP (Faturamento)', fieldName: 'billing-zip' },
        ];

        const failed = rules.filter(r => r.condition);
        errorFields.value = failed.map(r => ({ fieldName: r.fieldName, message: 'Campo obrigatório' }));

        if (failed.length > 0) {
            toast.error(`Campos obrigatórios não preenchidos: ${failed.map(r => r.label).join(', ')}`);
            return false;
        }

        if (f.installments && f.installments.length > 0 && f.receivables?.items?.length > 0) {
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            const invalid = f.receivables.items.filter(item =>
                !item.operation_date || item.operation_date.trim() === '' || item.operation_date.trim() === '-' || !dateRegex.test(item.operation_date.trim()) ||
                !item.present_value || item.present_value <= 0
            );
            if (invalid.length > 0) {
                toast.error(`${invalid.length} recebível(is) com "Data Operação Agrolend" ou "Valor Presente" inválidos. Processe a planilha antes de finalizar.`);
                return false;
            }
        }
        return true;
    }, [form, selectedDestinatario]);

    const { handleCancelContract, handleFinishContract, handleBillingProcess } = useContractActions({
        apiUrl: CONTRACTS_API_URL || '',        apiKey: CONTRACTS_API_KEY || '',
        creditOperationId: credit_operation_id,
        setLoading: (v: boolean) => { isLoading.value = v; },
        hasUnsavedChanges,
        hasInvalidDueDates,
        validateAllFields,
        setIsCancelModalOpen,
        setIsFinishModalOpen,
    });

    const [recipientData, setRecipientData] = useState<PersonSearchResult | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReceivable, setSelectedReceivable] = useState<any>(null);
    const [isReceivableModalOpen, setIsReceivableModalOpen] = useState(false);
    const { guarantors, products, setProducts, fetchPersons, fetchProducts } = useOperationData({
        personServiceUrl: PERSON_SERVICE_URL,
        personApiKey: API_KEY_PERSON,
        contractsApiUrl: CONTRACTS_API_URL,
        contractsApiKey: CONTRACTS_API_KEY
    });
    const [assignorParticipant, setAssignorParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const [recipientParticipant, setRecipientParticipant] = useState<{ uuid: string; name: string; document: string; id: number | null }>({ uuid: '', name: '', document: '', id: null });
    const productManagement = useProductManagement({
        apiUrl: CONTRACTS_API_URL,
        apiKey: CONTRACTS_API_KEY,
        setProducts,
        onProductCreated: (product) => {
            form.value = { ...form.value, crop_type: { id: product.uuid, name: product.name } };
        }
    });

    const { submitContract } = useContractSubmit({
        apiUrl: CONTRACTS_API_URL,
        apiKey: CONTRACTS_API_KEY,
        endpoint: '/contracts/receivables_assignment/update',
        setLoading: (v: boolean) => { isLoading.value = v; }
    });

    const handleSelectPerson = (selected: PersonSearchResult) => {
        const cnpjDoc = selected.documents?.find((doc) => doc.type === 'CNPJ');
        const cpfDoc = selected.documents?.find((doc) => doc.type === 'CPF');
        const document = cnpjDoc?.number || cpfDoc?.number || '';

        if (!document || document.trim() === '') {
            toast.error("Beneficiário sem documento cadastrado");
            return;
        }

        setSelectedDestinatario(selected.uuid);
        form.value = { ...form.value, recipient: selected.name || '' };

        setRecipientData(selected);
    };

    const fetchContractData = async () => {
        if (!credit_operation_id) {
            return;
        }
        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            return;
        }

        try {
            isLoading.value = true;
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());
            const response = await fetch(`${CONTRACTS_API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': CONTRACTS_API_KEY || ''
                }
            });

            if (response.ok) {
                const data: ContractApiResponse = await response.json();
                const schema = data.operation_dictionary || {};

                if (data.apiName && data.apiVersion) {
                    apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
                }

                const mapped = mapAcknowledgementDebtApiResponseToForm(data, form.value);

                if (mapped.assignorPart) {
                    setAssignorParticipant({
                        uuid: mapped.assignorPart.person_id || '00000000-0000-0000-0000-000000000000',
                        name: mapped.assignorPart.name || '',
                        document: mapped.assignorPart.document || '',
                        id: mapped.assignorPart.id ? Number(mapped.assignorPart.id) : null
                    });
                }

                if (mapped.recipientParticipant) {
                    setSelectedDestinatario(mapped.recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000');
                    setRecipientParticipant({
                        uuid: mapped.recipientParticipant.person_id || '00000000-0000-0000-0000-000000000000',
                        name: mapped.recipientParticipant.name || '',
                        document: mapped.recipientParticipant.document || '',
                        id: mapped.recipientParticipant.id ? Number(mapped.recipientParticipant.id) : null
                    });
                }

                form.value = mapped.form;

                if (schema.files) {
                    setExistingContractFile(schema.files.contract_file || '');
                    setExistingArtifactFiles(schema.files.artefacts || []);
                }

            } else {
                toast.error('Erro ao carregar dados da operação');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            isLoading.value = false;
            setHasUnsavedChanges(false); // Marcar como salvo após carregar
        }
    };

    useEffect(() => {
        const fetchProvincesData = async () => {
            try {
                const provincesData: ProvinceResponse[] = await fetchProvinces();
                const provinceItems = provincesData.map((item: ProvinceResponse) => ({
                    value: item.initials,
                    label: item.name,
                    initials: item.initials
                }));
                setProvinces(provinceItems);
                return provinceItems;
            } catch {
                return [];
            }
        };

        const loadDataSequentially = async () => {
            const provincesData = await fetchProvincesData();

            if (credit_operation_id) {
                await fetchContractData();
                if (form.value.operation_uf) {
                    const selectedProvince = provincesData.find((p: { value: string; label: string; initials: string }) => p.value === form.value.operation_uf);

                    if (selectedProvince) {
                        fetchCitiesWrapper(selectedProvince.initials);
                    }
                }
            }
        };

        fetchPersons();
        fetchProducts();
        loadDataSequentially();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credit_operation_id]);

    const handleProvinceChange = async (initials: string) => {
        fetchCitiesWrapper(initials);
        if (initials) {
            provinces.find(p => p.initials === initials);
            form.value = {
                ...form.value,
                operation_uf: initials,
                operation_city: ''
            };
        } else {
            setCities([]);
            form.value = {
                ...form.value,
                operation_uf: '',
                operation_city: ''
            };
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            if (initials) {

                const citiesData: CityResponse[] = await fetchCities(initials);
                const cityItems = citiesData.map((item: CityResponse) => ({
                    value: item.name,
                    label: item.name
                }));
                setCities(cityItems);
            }
        } catch (error: unknown) {
        }
    };





    const handleJsonDataLoaded = (data: JsonUploadData) => {
        const receivablesData = data.duplicates || data.receivables;
        if (receivablesData?.items) {
            const mappedItems: ReceivableItem[] = receivablesData.items.map(mapReceivableFromApi);

            form.value = {
                ...form.value,
                operation_number: data.operation_code || form.value.operation_number,
                receivables: {
                    quantity: receivablesData.quantity || receivablesData.items.length,
                    total: receivablesData.total || 0,
                    items: mappedItems
                }
            };
        }
    };

    useReceivablesDownloader(
        form.value.sequencial_number || 0,
        form.value.receivables?.items?.filter(item =>
            item.document_id && item.document_id.trim() !== '' && item.receivable_amount > 0
        ).length || 0,
        handleJsonDataLoaded
    );

    const isFormDisabled = isContractDisabled(form.value.status);

    const buildParticipants = () => {
        const recipientDocument = recipientData?.documents?.find((doc) => doc.type === 'CNPJ')?.number ||
            recipientData?.documents?.find((doc) => doc.type === 'CPF')?.number ||
            recipientParticipant.document || '';

        return generateParticipants({
            assignorName: form.value.assignor,
            assignorParticipant,
            recipientName: form.value.recipient,
            recipientParticipant,
            recipientDocument,
            selectedBeneficiary: selectedDestinatario,
            guarantors: form.value.guarantors.items,
            conditionalPersonId: true,
        });
    };

    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!CONTRACTS_API_URL || !CONTRACTS_API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        if (!form.value.original_contract_discharged || form.value.original_contract_discharged.length === 0) {
            toast.error('Informe pelo menos uma operação quitada');
            return;
        }

        if (!selectedDestinatario || selectedDestinatario === '00000000-0000-0000-0000-000000000000') {
            toast.error('Selecione um beneficiário');
            return;
        }

        if (!form.value.external_link || form.value.external_link.trim() === '') {
            toast.error('Card JIRA é obrigatório');
            return;
        }

        if (!form.value.receiving_account_name || form.value.receiving_account_name.trim() === '') {
            toast.error('Conta para Recebimento é obrigatória');
            return;
        }

        // Validar Valor Nominal obrigatório
        if (!form.value.disbursement.totalAmount || form.value.disbursement.totalAmount <= 0) {
            toast.error('Valor Nominal é obrigatório');            return;
        }

        // Validar se soma dos desembolsos não excede o Valor Nominal
        const sumDisbursements = form.value.disbursement.disbursements.reduce((sum, d) => sum + d.amount, 0);
        if (sumDisbursements > form.value.disbursement.totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${sumDisbursements.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${form.value.disbursement.totalAmount.toFixed(2)})`);
            return;
        }

        // Validar planilha processada
        if (form.value.receivables?.items?.some(item => !item.receivable_due_date || item.receivable_due_date.trim() === '')) {
            toast.error('Existem recebíveis sem data de vencimento. Corrija antes de salvar.');
            return;
        }

        if (form.value.receivables?.spreadsheet_processed) {
            const receivablesCount = form.value.receivables.items?.length || 0;
            const spreadsheetCount = form.value.receivables.spreadsheet_items_count || 0;
            const hasDivergences = form.value.receivables.items?.some(item => item.has_value_divergence) || false;

            if (receivablesCount !== spreadsheetCount) {
                toast.error(`Planilha processada: ${spreadsheetCount} itens na planilha, mas ${receivablesCount} recebíveis na lista. Todos os itens devem estar presentes.`);
                return;
            }

            if (hasDivergences) {
                toast.error('Planilha processada com divergências de valores. Corrija as divergências antes de salvar.');
                return;
            }
        }


        try {
            isLoading.value = true;

            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const dueDateValue = lastInstallmentDueDate || form.value.expiration_date || null;

            const payload = buildAcknowledgementDebtPayload({
                form: form.value,
                operationType: operationType || "ACKNOWLEDGEMENT_OF_DEBT",
                creditOperationId: credit_operation_id,
                selectedDestinatario,
                lastInstallmentDueDate: dueDateValue || undefined,
                participants: buildParticipants(),
                artifactFiles: artifactFiles.value,
                contractFiles: contractFiles.value,
                existingArtifactFiles,
                removedArtifactFiles,
                existingContractFile,
                removedContractFile,
            });

            await submitContract(
                payload,
                { contractFiles: contractFiles.value, artifactFiles: artifactFiles.value },
                {
                    successMessage: credit_operation_id
                        ? "Confissão de dívida atualizado com sucesso!"
                        : "Confissão de dívida criado com sucesso!",
                    onSuccess: async () => {
                        setHasUnsavedChanges(false);
                        if (credit_operation_id) {
                            await fetchContractData();
                            contractFiles.value = [];
                            artifactFiles.value = [];
                            setRemovedContractFile(false);
                            setRemovedArtifactFiles([]);
                        } else {
                            navigate(-1);
                        }
                    },
                    onError: (errors) => {
                        errorFields.value = errors;
                    }
                }
            );
        } finally {
            isLoading.value = false;
        }
    };

    return (
        <Fragment>
            <PageForm
                pageTitle="Confissão de Dívida"
                apiVersion={apiVersion.value}
                isLoading={isLoading.value}
                breadcrumb={[
                    { label: 'Contratos', url: OPERATIONS_LIST_URL },
                    { label: 'Confissão de Dívida', url: '#' }
                ]}
                controlSlot={
                    <div className="pt-4 flex justify-end">
                        <button
                            className="btn-cancel"
                            onClick={() => navigate(-1)}
                            disabled={isLoading.value}
                        >
                            Voltar
                        </button>

                        <ContractActionDropdown
                            onSave={handleSubmit}
                            onCancel={() => setIsCancelModalOpen(true)}
                            onFinish={() => setIsFinishModalOpen(true)}
                            onBilling={handleBillingProcess}
                            isLoading={isLoading.value}
                            isFormDisabled={isFormDisabled}
                            hasContractId={!!credit_operation_id}
                        />
                    </div>
                }
            >
                <div className="container mx-auto px-2 py-4">
                    <h2 className="text-xl font-semibold mt-4 mb-4">Informações Gerais</h2>
                    <div className="mb-4">
                        <BadgeStatus
                            color={getStatusColor(form.value.status)}
                            text={form.value.status || ''}
                        />
                    </div>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-2">
                            <TextEdit
                                id="sequencial_number"
                                label="Número do Contrato"
                                value={form.value.sequencial_number?.toString() || ''}
                                disabled={true}
                            />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4 mb-4">Dados Gerais</h2>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Operações Quitadas <span className="text-red-600">*</span>
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                className="w-48 h-[42px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                placeholder="Digite o número"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        const value = Number.parseInt((e.target as HTMLInputElement).value);
                                        if (value > 0 && !form.value.original_contract_discharged.includes(value)) {
                                            updateForm({ original_contract_discharged: [...form.value.original_contract_discharged, value] });
                                            (e.target as HTMLInputElement).value = '';
                                        }
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={(e) => {
                                    const input = (e.target as HTMLButtonElement).parentElement?.querySelector('input') as HTMLInputElement;
                                    const value = Number.parseInt(input.value);
                                    if (value > 0 && !form.value.original_contract_discharged.includes(value)) {
                                        updateForm({ original_contract_discharged: [...form.value.original_contract_discharged, value] });
                                        input.value = '';
                                    }
                                }}
                                className="h-[42px] px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Adicionar
                            </button>
                            <div className="flex flex-wrap gap-2 flex-1">
                                {form.value.original_contract_discharged.map((id, index) => (
                                    <span key={index} className="inline-flex items-center gap-1 h-[42px] px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded">
                                        {id}
                                        <button
                                            type="button"
                                            onClick={() => updateForm({ original_contract_discharged: form.value.original_contract_discharged.filter((_, i) => i !== index) })}
                                            className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                        {form.value.original_contract_discharged.length === 0 && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-2">Adicione pelo menos uma operação quitada</p>
                        )}
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="operation_number"
                                label="Número da Operação"
                                value={form.value.operation_number}
                                onChange={(e) => updateForm({ operation_number: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "operation_number")}
                            />
                        </div>
                        <div className="col-span-2">
                            <TextEdit
                                id="external_link"
                                label="Card JIRA"
                                value={form.value.external_link}
                                onChange={(e) => updateForm({ external_link: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "external_link")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="credit_product_type_name"
                                label="Tipo de Operação de Crédito"
                                value={form.value.credit_product_type_name}
                                onChange={(e) => updateForm({ credit_product_type_name: e.target.value })}
                                items={CREDIT_OPERATION_TYPE_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "credit_product_type_name")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="receiving_account_name"
                                label="Conta para Recebimento"
                                value={form.value.receiving_account_name || ''}
                                onChange={(e) => updateForm({ receiving_account_name: e.target.value })}
                                items={RECEIVING_ACCOUNT_OPTIONS}
                                errorMessage={getErrorMessage(errorFields.value, "receiving_account_name")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <TextEdit
                                id="originador"
                                label="Originador"
                                value={form.value.originator}
                                onChange={(e) => updateForm({ originator: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "originator")}
                            />
                        </div>
                        <div className="col-span-2">
                            <SelectTextField
                                id="operation_uf"
                                label="UF da Operação"
                                value={form.value.operation_uf.toString()}
                                items={provinces}
                                onChange={(e) => handleProvinceChange(e.target.value)}
                                errorMessage={getErrorMessage(errorFields.value, "operation_uf")}
                            />
                        </div>
                        <div className="col-span-3">
                            <SelectTextField
                                id="operation_city"
                                label="Cidade da Operação"
                                value={form.value.operation_city}
                                items={cities}
                                onChange={(e) => updateForm({ operation_city: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "operation_city")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-4">
                            <TextEdit
                                id="assignor"
                                label="Devedor"
                                value={form.value.assignor}
                                onChange={(e) => updateForm({ assignor: e.target.value })}
                                errorMessage={getErrorMessage(errorFields.value, "assignor")}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="emission_date"
                                label="Data de Emissão"
                                value={form.value.emission_date}
                                onChange={(e) => updateForm({ emission_date: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (!isValidDate(e.target.value)) {
                                        toast.error('Data de Emissão inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "emission_date")}
                            />
                        </div>
                        <div className="col-span-4">
                            <TextEdit
                                id="approved_on"
                                label="Data de Aprovação"
                                value={form.value.approved_on}
                                onChange={(e) => updateForm({ approved_on: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (e.target.value && !isValidDate(e.target.value)) {
                                        toast.error('Data de Aprovação inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "approved_on")}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-6">
                            <TextEdit
                                id="recipient"
                                label="Beneficiário"
                                value={form.value.recipient}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 mb-4 h-10 w-[75px] flex items-center justify-center"
                                title="Buscar beneficiário"
                            >
                                <FaSearch />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-2 mb-6">
                        <div className="col-span-6">
                            <SelectTextField
                                id="product_id"
                                label="Produto"
                                value={form.value.crop_type.id}
                                items={products}
                                onChange={(e) => {
                                    const selected = products.find(product => product.value === e.target.value);
                                    form.value = {
                                        ...form.value,
                                        crop_type: {
                                            id: e.target.value,
                                            name: selected?.label || ''
                                        }
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "product")}
                            />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button
                                onClick={() => productManagement.setIsProductModalOpen(true)}
                                className="btn-add mb-4 h-10 w-[75px]"
                                title="Novo produto"
                            >
                                Novo
                            </button>
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4 mb-4 dark:text-white">Valor Financiado</h2>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="loan_amount"
                                label="Valor Financiado"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.loan_amount}
                                onChange={(value) => {
                                    updateForm({ loan_amount: value });
                                }}
                            />
                        </div>
                    </div>

                    <BaseCalculationSection
                        value={form.value.base_calculation}
                        onChange={(updated) => updateForm({ base_calculation: updated })}
                    />

                    <BillingConfigSection
                        value={form.value.billing_config || EMPTY_BILLING_CONFIG}
                        onChange={(updated) => updateForm({ billing_config: updated })}
                        disabled={isFormDisabled}
                        errorFields={errorFields.value}
                    />

                    <AssignorBillingAddress
                        value={form.value.assignor_billing_address || EMPTY_ASSIGNOR_BILLING_ADDRESS}
                        onChange={(updated) => updateForm({ assignor_billing_address: updated })}
                        disabled={isFormDisabled}
                        assignorPersonId={assignorParticipant.uuid}
                        assignorName={assignorParticipant.name}
                        assignorDocument={assignorParticipant.document}
                    />

                    <DisbursementManager
                        disbursements={form.value.disbursement.disbursements}
                        totalAmount={form.value.disbursement.totalAmount}
                        onTotalAmountChange={(value) => {
                            form.value = {
                                ...form.value,
                                total_amount: value,
                                disbursement: {
                                    ...form.value.disbursement,
                                    totalAmount: value
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onAdd={(disbursement) => {
                            const currentDisbursements = [...form.value.disbursement.disbursements, disbursement];
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements: currentDisbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(index, disbursement) => {
                            const disbursements = form.value.disbursement.disbursements.map((d, i) => i === index ? disbursement : d);
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                disbursement: { ...form.value.disbursement, disbursements }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-2">
                            <CurrencyInput
                                id="totalAmount"
                                label="Total dos Desembolsos"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.disbursement.totalAmount - (form.value.rebate_amount || 0)}
                                disabled={true}
                            />
                        </div>
                        <div className="col-span-2">
                            <CurrencyInput
                                id="rebate_amount"
                                label="Valor de Abatimento"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.rebate_amount}
                                onChange={(value) => updateForm({ rebate_amount: value })}
                                errorMessage={getErrorMessage(errorFields.value, "rebate_amount")}
                            />
                        </div>
                    </div>

                    <FeesManager
                        fees={form.value.fees}
                        onAdd={(fee) => {
                            const newItems = [...form.value.fees.items, fee];
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(index, fee) => {
                            const items = form.value.fees.items.map((f, i) => i === index ? fee : f);
                            form.value = {
                                ...form.value,
                                fees: {
                                    items,
                                    quantity: items.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.fees.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                fees: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <h2 className="text-xl font-semibold mt-4 mb-4">Vencimento</h2>
                    <div className="grid grid-cols-12 gap-4 mb-6">
                        <div className="col-span-3">
                            <CurrencyInput
                                id="total_amount"
                                label="Valor Nominal *"
                                prefix="R$"
                                fractionDigits={2}
                                value={form.value.total_amount}
                                onChange={(value) => updateForm({ total_amount: value })}
                                errorMessage={getErrorMessage(errorFields.value, "total_amount")}
                                disabled={false}
                            />
                        </div>
                        <div className="col-span-3">
                            <TextEdit
                                id="expiration_date"
                                label="Data de vencimento"
                                value={form.value.expiration_date}
                                onChange={(e) => updateForm({ expiration_date: applyDateMask(e.target.value) })}
                                onBlur={(e) => {
                                    if (!isValidDate(e.target.value)) {
                                        toast.error('Data de vencimento inválida');
                                        e.target.focus();
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "expiration_date")}
                                disabled={!form.value.installments.some(inst => inst.format === 'PERCENTUAL')}
                            />
                        </div>
                    </div>

                    <InstallmentManager
                        installments={form.value.installments}
                        onAdd={(installment) => {
                            form.value = {
                                ...form.value,
                                installments: [...form.value.installments, installment]
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdateItem={(index, installment) => {
                            const installments = form.value.installments.map((inst, i) => i === index ? installment : inst);
                            form.value = {
                                ...form.value,
                                installments
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            form.value = {
                                ...form.value,
                                installments: form.value.installments.filter((_, i) => i !== index)
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onUpdate={(updatedInstallments, lastDueDate) => {
                            setLastInstallmentDueDate(lastDueDate);
                            form.value = {
                                ...form.value,
                                installments: updatedInstallments,
                                expiration_date: lastDueDate || form.value.expiration_date
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <GuarantorsManager
                        guarantors={form.value.guarantors}
                        guaranteeValue={form.value.guarantee_value}
                        onGuaranteeValueChange={(value) => updateForm({ guarantee_value: value })}
                        onAdd={(guarantor) => {
                            const newItems = [...form.value.guarantors.items, guarantor];
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        onRemove={(index) => {
                            const newItems = form.value.guarantors.items.filter((_, i) => i !== index);
                            form.value = {
                                ...form.value,
                                guarantors: {
                                    items: newItems,
                                    quantity: newItems.length
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        disabled={isLoading.value}
                    />

                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-6">
                            <SelectTextField
                                id="main_risk_uuid"
                                label="Risco Principal"
                                value={form.value.main_risk.uuid}
                                items={[
                                    { value: assignorParticipant.uuid || '00000000-0000-0000-0000-000000000000', label: assignorParticipant.name || form.value.assignor || 'Devedor' },
                                    ...form.value.guarantors.items.map(g => ({ value: g.uuid, label: g.name }))
                                ]}
                                onChange={(e) => {
                                    if (e.target.value === 'assignor') {
                                        form.value = {
                                            ...form.value,
                                            main_risk: {
                                                uuid: '00000000-0000-0000-0000-000000000000',
                                                name: form.value.assignor || 'Devedor'
                                            }
                                        };
                                        setHasUnsavedChanges(true);
                                    } else {
                                        const selected = form.value.guarantors.items.find((g: any) => g.uuid === e.target.value);
                                        form.value = {
                                            ...form.value,
                                            main_risk: {
                                                uuid: e.target.value,
                                                name: selected?.name || ''
                                            }
                                        };
                                        setHasUnsavedChanges(true);
                                    }
                                }}
                                errorMessage={getErrorMessage(errorFields.value, "main_risk")}
                            />
                        </div>
                    </div>

                    <h2 className="text-xl font-semibold mt-4 mb-4">Contrato</h2>
                    <div>
                        <div
                            className="flex items-center justify-center w-full"
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.dataTransfer.files?.[0]) {
                                    contractFiles.value = [e.dataTransfer.files[0]];
                                }
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <label htmlFor="contract-upload"
                                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                    </svg>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Upload do Contrato</span>
                                    </p>
                                </div>
                                <input
                                    id="contract-upload"
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            contractFiles.value = [file];
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        {existingContractFile && !removedContractFile && (
                            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-800 border border-blue-200 dark:border-blue-600 rounded flex justify-between items-center">
                                <p className="text-sm text-blue-700 dark:text-blue-100">
                                    <strong>Arquivo atual:</strong>{' '}
                                    <button
                                        onClick={() => handleDownload(credit_operation_id || '', existingContractFile)}
                                        className="underline hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer bg-transparent border-0 p-0"
                                    >
                                        {existingContractFile}
                                    </button>
                                </p>
                                <button
                                    onClick={() => setRemovedContractFile(true)}
                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                >
                                    <FaTimes className="inline mr-1" /> Remover
                                </button>
                            </div>
                        )}
                        {removedContractFile && (
                            <div className="mt-2 p-2 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 rounded flex justify-between items-center">
                                <p className="text-sm text-red-700 dark:text-red-100">
                                    <strong>Arquivo removido:</strong> {existingContractFile}
                                </p>
                                <button
                                    onClick={() => setRemovedContractFile(false)}
                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                >
                                    Restaurar
                                </button>
                            </div>
                        )}
                        {contractFiles.value.length > 0 && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-800 border border-green-200 dark:border-green-600 rounded">
                                <p className="text-sm text-green-700 dark:text-green-100">
                                    <strong>Novo arquivo:</strong> {contractFiles.value[0].name}
                                </p>
                            </div>
                        )}
                    </div>

                    <ArtifactFilesUpload
                        artifactFiles={artifactFiles}
                        existingFiles={existingArtifactFiles}
                        removedFiles={removedArtifactFiles}
                        onRemoveExisting={(file) => setRemovedArtifactFiles([...removedArtifactFiles, file])}
                        onRestoreExisting={(file) => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== file))}
                        creditOperationId={credit_operation_id || ''}
                        onDownload={handleDownload}
                    />

                    <ReceivablesManager
                        receivables={form.value.receivables?.items || []}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onRowClick={(receivable) => {
                            setSelectedReceivable(receivable);
                            setIsReceivableModalOpen(true);
                        }}
                        onReceivablesUpdate={(updatedReceivables, spreadsheetProcessed, spreadsheetItemsCount) => {
                            form.value = {
                                ...form.value,
                                receivables: {
                                    ...form.value.receivables,
                                    items: updatedReceivables,
                                    spreadsheet_processed: spreadsheetProcessed,
                                    spreadsheet_items_count: spreadsheetItemsCount
                                }
                            };
                            setHasUnsavedChanges(true);
                        }}
                        spreadsheetProcessed={form.value.receivables?.spreadsheet_processed || false}
                        onValidationChange={setHasInvalidDueDates}
                        overcollateral={form.value.receivables?.overcollateral}
                        onOvercollateralChange={(value) => { form.value = { ...form.value, receivables: { ...form.value.receivables, overcollateral: value } }; setHasUnsavedChanges(true); }}
                        overcollateralError={getErrorMessage(errorFields.value, "overcollateral")}
                        convertButton={
                            <ConvertReceivablesToInstallment
                                receivables={form.value.receivables?.items || []}
                                onConvert={(installments) => {
                                    const lastDueDate = installments.length > 0
                                        ? installments[installments.length - 1].dueDate
                                        : undefined;
                                    setLastInstallmentDueDate(lastDueDate);
                                    form.value = {
                                        ...form.value,
                                        installments,
                                        expiration_date: lastDueDate || form.value.expiration_date
                                    };
                                    setHasUnsavedChanges(true);
                                }}
                                disabled={isLoading.value}
                            />
                        }
                    />
                </div>

                <PersonSearchModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSelect={handleSelectPerson}
                />

                {/* Modal de criação de produto */}
                {productManagement.isProductModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold dark:text-white">Novo Produto</h3>
                                <button
                                    onClick={() => {
                                        productManagement.setIsProductModalOpen(false);
                                        productManagement.setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="space-y-4">
                                <TextEdit
                                    label="Nome"
                                    value={productManagement.newProduct.name}
                                    onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, name: e.target.value })}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                                    <textarea
                                        rows={4}
                                        value={productManagement.newProduct.description}
                                        onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, description: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Padrão de Qualidade</label>
                                    <textarea
                                        rows={4}
                                        value={productManagement.newProduct.quality_standard}
                                        onChange={(e) => productManagement.setNewProduct({ ...productManagement.newProduct, quality_standard: e.target.value })}
                                        className="block w-full text-sm text-gray-900 dark:text-gray-100 bg-transparent border border-gray-300 dark:border-gray-600 rounded p-2 focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 resize-none"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    onClick={() => {
                                        productManagement.setIsProductModalOpen(false);
                                        productManagement.setNewProduct({ name: '', description: '', quality_standard: '' });
                                    }}
                                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                                    disabled={productManagement.isCreatingProduct}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={productManagement.handleCreateProduct}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={productManagement.isCreatingProduct}
                                >
                                    {productManagement.isCreatingProduct ? 'Criando...' : 'Criar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onCancel={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelContract}
                id="cancel-contract-modal"
                title="Cancelar Contrato"
                text="Tem certeza que deseja cancelar este contrato?"
            />
            <ConfirmationModal
                isOpen={isFinishModalOpen}
                onCancel={() => setIsFinishModalOpen(false)}
                onConfirm={handleFinishContract}
                id="finish-contract-modal"
                title="Finalizar Contrato"
                text="Tem certeza que deseja finalizar este contrato?"
            />
            <ReceivableModalView
                isOpen={isReceivableModalOpen}
                onClose={() => {
                    setIsReceivableModalOpen(false);
                    setSelectedReceivable(null);
                }}
                receivable={selectedReceivable}
            />
        </Fragment>);
};

export default AcknowledgementDebtPage;